import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import useAuth from '../../../hooks/useAuth';
import operationsService from '../../../services/operationsService';
import unifiedSocket from '../../../services/unifiedSocket';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';

function healthChip(value) {
  const v = String(value || '').toLowerCase();
  if (v === 'ok') return <Chip size="small" color="success" label="OK" />;
  if (v === 'down') return <Chip size="small" color="error" label="DOWN" />;
  if (v === 'unknown') return <Chip size="small" color="warning" label="UNKNOWN" />;
  return <Chip size="small" label={value || '—'} />;
}

export default function OmniDashboardPage() {
  const { user: authUser } = useAuth();
  const currentUser = authUser?.user || authUser || null;
  const token = authUser?.token || null;
  const socketUserId = currentUser?.id || null;
  const companyId = currentUser?.companyId || 1;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await operationsService.getOmniHealth().catch(() => null);
      setData(resp?.data?.data || resp?.data || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!token || !socketUserId) return;
    unifiedSocket.connect({ id: socketUserId, companyId, token });
    const socket = unifiedSocket.getSocket?.();
    if (!socket) return;

    const onChannelHealth = () => {
      load().catch(() => {});
    };
    const onQueueEvent = () => {
      load().catch(() => {});
    };
    const onAlert = (payload) => {
      setAlerts(prev => {
        const next = [{ ...payload, at: payload?.at || new Date().toISOString() }, ...prev];
        return next.slice(0, 20);
      });
    };

    socket.on('omni.channelHealth', onChannelHealth);
    socket.on('externalQueue.event', onQueueEvent);
    socket.on('omni.alert', onAlert);
    return () => {
      try { socket.off('omni.channelHealth', onChannelHealth); } catch {}
      try { socket.off('externalQueue.event', onQueueEvent); } catch {}
      try { socket.off('omni.alert', onAlert); } catch {}
    };
  }, [token, socketUserId, companyId, load]);

  const channels = Array.isArray(data?.channels) ? data.channels : [];
  const queues = data?.queues || {};
  const smsGateways = Array.isArray(data?.smsAndroid?.gateways) ? data.smsAndroid.gateways : [];

  const channelColumns = useMemo(() => ([
    { key: 'kind', label: 'Canal' },
    { key: 'name', label: 'Conexão' },
    { key: 'health', label: 'Health', render: (v) => healthChip(v) },
    { key: 'status', label: 'Status' },
    { key: 'checkedAt', label: 'Última checagem', render: (v) => v ? new Date(v).toLocaleString() : '—' }
  ]), []);

  const queueColumns = useMemo(() => ([
    { key: 'queue', label: 'Fila' },
    { key: 'waiting', label: 'Waiting', align: 'right' },
    { key: 'active', label: 'Active', align: 'right' },
    { key: 'delayed', label: 'Delayed', align: 'right' },
    { key: 'failed', label: 'Failed', align: 'right' },
    { key: 'completed', label: 'Completed', align: 'right' }
  ]), []);

  const queueRows = useMemo(() => {
    const names = ['system', 'whatsapp', 'sms', 'webhooks'];
    return names.map(q => {
      const c = queues?.[q] || {};
      return {
        queue: q,
        waiting: Number(c.waiting || 0),
        active: Number(c.active || 0),
        delayed: Number(c.delayed || 0),
        failed: Number(c.failed || 0),
        completed: Number(c.completed || 0)
      };
    });
  }, [queues]);

  const alertColumns = useMemo(() => ([
    { key: 'at', label: 'Hora', render: (v) => v ? new Date(v).toLocaleString() : '—' },
    { key: 'kind', label: 'Tipo' },
    { key: 'channel', label: 'Canal' },
    { key: 'queue', label: 'Fila' },
    { key: 'name', label: 'Nome' }
  ]), []);

  const smsGatewayColumns = useMemo(() => ([
    { key: 'name', label: 'Gateway' },
    { key: 'deviceId', label: 'Device ID' },
    { key: 'status', label: 'Status' },
    { key: 'batteryPct', label: 'Bateria', align: 'right', render: (v) => (v === null || v === undefined) ? '—' : `${v}%` },
    { key: 'signalDbm', label: 'Sinal', align: 'right', render: (v) => (v === null || v === undefined) ? '—' : `${v} dBm` },
    { key: 'lastSeenAt', label: 'Último heartbeat', render: (v) => v ? new Date(v).toLocaleString() : '—' }
  ]), []);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">Omnichannel (Operacional)</Typography>
        <Chip size="small" label={`Empresa ${data?.companyId || companyId}`} />
      </Stack>

      {loading && <LoadingState label="Carregando..." />}

      {!loading && (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Canais</Typography>
            <DataTable columns={channelColumns} rows={channels} getKey={(r) => r.connectionId} />
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Filas / Workers</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
              <Chip size="small" label={`worker system: ${data?.workers?.system ?? '—'}`} />
              <Chip size="small" label={`worker whatsapp: ${data?.workers?.whatsapp ?? '—'}`} />
              <Chip size="small" label={`worker sms: ${data?.workers?.sms ?? '—'}`} />
              <Chip size="small" label={`worker webhooks: ${data?.workers?.webhooks ?? '—'}`} />
              <Chip size="small" label={`online system: ${data?.workers?.online?.system ?? '—'}`} />
              <Chip size="small" label={`online whatsapp: ${data?.workers?.online?.whatsapp ?? '—'}`} />
              <Chip size="small" label={`online sms: ${data?.workers?.online?.sms ?? '—'}`} />
              <Chip size="small" label={`online webhooks: ${data?.workers?.online?.webhooks ?? '—'}`} />
              <Chip size="small" label={`sms: ${data?.sms?.configured ? 'configurado' : 'não configurado'}`} color={data?.sms?.configured ? 'success' : 'warning'} />
              <Chip size="small" label={`sms android online: ${data?.smsAndroid?.online ?? '—'}`} color={(data?.smsAndroid?.online ?? 0) > 0 ? 'success' : 'default'} />
            </Stack>
            <DataTable columns={queueColumns} rows={queueRows} getKey={(r) => r.queue} />
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Gateways SMS Android</Typography>
            <DataTable columns={smsGatewayColumns} rows={smsGateways} getKey={(r) => r.id} />
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Alertas (tempo real)</Typography>
            <DataTable columns={alertColumns} rows={alerts} getKey={(r) => `${r.kind}_${r.at}_${Math.random()}`} />
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
